import React from "react";

interface FieldGroupProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

/** A titled block of fields inside a shell section. */
export function FieldGroup({
  title,
  description,
  children,
  className,
}: FieldGroupProps) {
  return (
    <div className={`space-y-3 ${className ?? ""}`}>
      <div>
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        )}
      </div>
      {children}
    </div>
  );
}

/** Responsive 1/2/3-column grid for form fields. */
export function FieldGrid({
  cols = 2,
  children,
  className,
}: {
  cols?: 1 | 2 | 3;
  children: React.ReactNode;
  className?: string;
}) {
  const colClass =
    cols === 1
      ? "grid-cols-1"
      : cols === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1 md:grid-cols-3";
  return (
    <div className={`grid ${colClass} gap-x-6 ${className ?? ""}`}>
      {children}
    </div>
  );
}

/** Read-only value styled like a disabled input, for non-editable fields. */
export function ReadOnlyField({ value }: { value?: string }) {
  return (
    <div className="input flex h-9 items-center border-gray-200 bg-gray-50 px-3 text-gray-500">
      {value || "—"}
    </div>
  );
}

/** Simple definition row used by summary/review cards. */
export function SummaryRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2 py-0.5 text-xs">
      <span className="w-32 shrink-0 text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 text-gray-700">{children}</span>
    </div>
  );
}
